// AiUsersStatusView — admin tem visão de "quem está usando IA, como e qual modelo".
//
// 3 colunas no header: usuários totais, Google conectado, BYOK em uso (na última chamada).
// Tabela abaixo: usuário, fonte da IA (Google OAuth / BYOK / Plataforma), modelo.
// Filtros: só "sem IA própria" (pra follow-up), só "conectados", etc.

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  Brain,
  Check,
  Cloud,
  Key,
  Loader2,
  RefreshCw,
  Search,
  Sparkles,
  X
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { api, ApiError } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";

type Status = "loading" | "ready" | "error";

interface AiUserRow {
  id: string;
  email: string;
  name: string;
  googleConnected: boolean;
  googleEmail: string | null;
  lastAiUsage: {
    source: "platform" | "byok" | "connected";
    provider: string;
    model: string;
    modelKind?: string;
    atIso: string;
    feature?: string;
  } | null;
  isSuspended: boolean;
}

interface FeatureConfigRow {
  featureKey: string;
  provider: string;
  model: string;
  apiKeySet: boolean;
  updatedAt: string | null;
}

const phase = signal<Status>("loading");
const rows = signal<AiUserRow[]>([]);
const configs = signal<FeatureConfigRow[]>([]);
const errMsg = signal<string | null>(null);

const filter = signal<"all" | "without_own" | "google" | "byok">("all");
const search = signal<string>("");

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listAiUsersStatus();
    rows.value = r.users;
    configs.value = r.featureConfigs;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

export function AiUsersStatusView() {
  useEffect(() => {
    void load();
  }, []);

  if (phase.value === "loading") {
    return (
      <div class="space-y-3">
        <Skeleton class="h-24 w-full" />
        <Skeleton class="h-64 w-full" />
      </div>
    );
  }
  if (phase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg.value}
      </div>
    );
  }

  const list = rows.value;
  const fr = filter.value;
  const q = search.value.trim().toLowerCase();
  const visible = useMemo(() => {
    return list.filter((u) => {
      if (u.isSuspended) return false;
      if (fr === "google" && !u.googleConnected) return false;
      if (fr === "byok" && u.lastAiUsage?.source !== "byok") return false;
      if (fr === "without_own") {
        const hasOwn =
          u.googleConnected || u.lastAiUsage?.source === "byok";
        if (hasOwn) return false;
      }
      if (q) {
        const hay = `${u.email} ${u.name}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [list, fr, q]);

  const counts = useMemo(() => {
    let total = 0,
      googleOn = 0,
      byok = 0,
      none = 0;
    for (const u of list) {
      if (u.isSuspended) continue;
      total++;
      if (u.googleConnected) googleOn++;
      else if (u.lastAiUsage?.source === "byok") byok++;
      else none++;
    }
    return { total, googleOn, byok, none };
  }, [list]);

  return (
    <div class="space-y-4">
      <div class="rounded-md border border-violet-500/30 bg-violet-500/5 px-4 py-3 text-sm">
        <Sparkles className="mr-1 inline size-4 text-violet-600" />
        <strong>Quem está usando IA, e como.</strong> Use o filtro "Sem IA
        própria" pra encontrar usuários que ainda dependem só da chave da
        plataforma — bom alvo pra mandar o passo-a-passo de criar uma chave
        Gemini grátis.
      </div>

      {/* KPIs */}
      <div class="grid gap-2 sm:grid-cols-4">
        <KpiCard label="Ativos" value={counts.total} icon={Brain} tone="primary" />
        <KpiCard
          label="Google/Gemini conectado"
          value={counts.googleOn}
          icon={Cloud}
          tone="emerald"
        />
        <KpiCard
          label="BYOK em uso"
          value={counts.byok}
          icon={Key}
          tone="violet"
        />
        <KpiCard
          label="Sem chave própria"
          value={counts.none}
          icon={AlertCircle}
          tone="amber"
        />
      </div>

      {/* Modelos rodando agora (plataforma) */}
      <FeatureConfigsSummary configs={configs.value} />

      {/* Toolbar */}
      <div class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[240px] flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            placeholder="Buscar email ou nome…"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
        <FilterPill
          label="Todos"
          active={fr === "all"}
          onClick={() => (filter.value = "all")}
        />
        <FilterPill
          label={`Sem chave própria (${counts.none})`}
          active={fr === "without_own"}
          onClick={() => (filter.value = "without_own")}
        />
        <FilterPill
          label={`Google (${counts.googleOn})`}
          active={fr === "google"}
          onClick={() => (filter.value = "google")}
        />
        <FilterPill
          label={`BYOK (${counts.byok})`}
          active={fr === "byok"}
          onClick={() => (filter.value = "byok")}
        />
        <button
          type="button"
          onClick={() => void load()}
          title="Atualizar"
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-accent"
        >
          <RefreshCw className="size-3" />
          Atualizar
        </button>
      </div>

      <p class="px-1 text-xs text-muted-foreground">
        {visible.length} de {counts.total} usuário{counts.total !== 1 ? "s" : ""}
      </p>

      {visible.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhum usuário encontrado nesse filtro.
        </div>
      ) : (
        <div class="overflow-hidden rounded-md border border-border bg-card/40">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border bg-card/60 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <th class="px-3 py-2">Usuário</th>
                <th class="px-3 py-2">Fonte da IA</th>
                <th class="px-3 py-2">Última feature/modelo</th>
                <th class="px-3 py-2">Último uso</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((u) => (
                <UserRow key={u.id} u={u} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UserRow({ u }: { u: AiUserRow }) {
  const source = u.googleConnected
    ? "connected"
    : u.lastAiUsage?.source ?? "none";

  return (
    <tr class="border-b border-border/60 last:border-0 hover:bg-accent/30">
      <td class="px-3 py-2">
        <div class="font-medium">{u.name}</div>
        <div class="font-mono text-[11px] text-muted-foreground">{u.email}</div>
      </td>
      <td class="px-3 py-2">
        {source === "connected" ? (
          <span
            class="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300"
            title={u.googleEmail ? `Conta: ${u.googleEmail}` : undefined}
          >
            <Cloud width="0.9em" height="0.9em" />
            Google/Gemini
          </span>
        ) : source === "byok" ? (
          <span
            class="inline-flex items-center gap-1 rounded-md bg-violet-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300"
            title="Aluno cola sua própria API Key"
          >
            <Key width="0.9em" height="0.9em" />
            BYOK
          </span>
        ) : source === "platform" ? (
          <span
            class="inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300"
            title="Usando chave compartilhada da plataforma"
          >
            <AlertCircle width="0.9em" height="0.9em" />
            Plataforma
          </span>
        ) : (
          <span
            class="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
            title="Aluno nunca usou IA — ou tracking ainda não capturou"
          >
            <X width="0.9em" height="0.9em" />
            sem dados
          </span>
        )}
      </td>
      <td class="px-3 py-2">
        {u.lastAiUsage ? (
          <>
            <div class="text-xs">
              <span class="font-mono text-[10px] text-muted-foreground">
                {u.lastAiUsage.feature ?? "—"}
              </span>
            </div>
            <div class="font-mono text-[11px]">
              {u.lastAiUsage.provider}/{u.lastAiUsage.model}
            </div>
          </>
        ) : (
          <span class="text-[11px] italic text-muted-foreground/70">—</span>
        )}
      </td>
      <td class="px-3 py-2 text-[11px] text-muted-foreground">
        {u.lastAiUsage
          ? new Date(u.lastAiUsage.atIso).toLocaleString("pt-BR")
          : "—"}
      </td>
    </tr>
  );
}

function FeatureConfigsSummary({ configs }: { configs: FeatureConfigRow[] }) {
  if (configs.length === 0) {
    return (
      <div class="rounded-md border border-dashed border-amber-500/40 bg-amber-500/5 px-4 py-3 text-xs text-amber-800 dark:text-amber-300">
        <AlertCircle className="mr-1 inline size-3.5" />
        Nenhuma feature configurada — alunos sem BYOK rodam no fallback de env
        var. Configure em "Modelos IA por feature".
      </div>
    );
  }
  return (
    <details class="rounded-md border border-border bg-card/40">
      <summary class="cursor-pointer px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        Modelos rodando agora ({configs.length}) — clique pra expandir
      </summary>
      <ul class="divide-y divide-border border-t border-border">
        {configs.map((c) => (
          <li
            key={c.featureKey}
            class="flex items-center gap-3 px-4 py-2 text-xs"
          >
            <code class="font-mono text-[10px] text-muted-foreground">
              {c.featureKey}
            </code>
            <span class="font-mono text-[11px]">
              {c.provider}/{c.model}
            </span>
            {c.apiKeySet ? (
              <span class="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700 dark:text-emerald-300">
                <Check className="size-2.5" />
                chave salva
              </span>
            ) : (
              <span class="ml-auto rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold text-amber-700 dark:text-amber-300">
                fallback env
              </span>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}

function KpiCard({
  label,
  value,
  icon: Icon,
  tone
}: {
  label: string;
  value: number;
  icon: typeof Brain;
  tone: "primary" | "emerald" | "violet" | "amber";
}) {
  const toneClass =
    tone === "emerald"
      ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
      : tone === "violet"
        ? "bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30"
        : tone === "amber"
          ? "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30"
          : "bg-primary/10 text-primary border-primary/30";
  return (
    <div class={`rounded-md border px-4 py-3 ${toneClass}`}>
      <div class="flex items-center gap-2">
        <Icon className="size-4" />
        <span class="text-[10px] font-bold uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div class="mt-1 text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function FilterPill({
  label,
  active,
  onClick
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${
        active
          ? "border-primary/50 bg-primary/10 text-primary"
          : "border-border bg-background/50 text-muted-foreground hover:bg-accent"
      }`}
    >
      {label}
    </button>
  );
}
